"use client";
import React from "react";
import { PostsGridOptionsType } from "@/types/ModuleOptionsType";
import PostCard, {
  PostsGridElementType,
} from "../../components/PostCard/PostCard";
import {
  Grid,
  GridContent,
  GridItem,
  GridLoadMore,
} from "@aleksundshantu/aunds-utils-react/primitives/grid";
import { fetchPosts } from "../../getData/fetchPosts";
import buttonStyles from "../../components/Button/Button.module.scss";
import { mockPost } from "@/ui/mock-data";
import { PostsGridProps } from "./PostGrid.server";
import { useGlobalContext } from "@aleksundshantu/aunds-utils-react/contexts";
import { Breakpoint } from "@aleksundshantu/aunds-utils-react/layout";
import { useAnimeKeyWithDep } from "@/hooks/useAnimeKey";
import GridCategoryFilter from "@/components/GridCategoryFilter/GridCategoryFilter.client";

type PostsGridModuleClientProps = Omit<PostsGridProps, "postsGridOptions"> & {
  postsGridOptions: {
    box: PostsGridOptionsType["box"];
    grid_options: PostsGridOptionsType["grid_options"];
    media_format: PostsGridOptionsType["media_format"];
  };
} & {
  locale: string;
  initialPosts: PostsGridElementType[];
  initialTotalPosts: number;
  itemWidth: Record<Breakpoint, string>;
  columnNumbers: Record<Breakpoint, number>;
  hidePagination?: boolean;
};

{
  /* jscpd:ignore-start */
}
const PostsGridModuleClient = ({
  data,
  postsGridOptions,
  fallback_image,
  categories,
  locale,
  initialPosts,
  initialTotalPosts,
  itemWidth,
  columnNumbers,
  excludePostIds,
  hidePagination,
}: PostsGridModuleClientProps) => {
  const { getLabel } = useGlobalContext();
  const [activeCategoryId, setActiveCategoryId] = React.useState<number | null>(
    null,
  );
  const {
    box: { show_border_radius, show_box_shadow, show_card_box },
    grid_options: { grid_navigation, fallback_options },
    media_format,
  } = postsGridOptions;

  const { id, posts_per_page } = data.layout;
  const scrollTargetSelector = id ? `#${id}` : undefined;
  const showCategoryFilter = data.grid?.type === "show-all";
  const refreshKey = useAnimeKeyWithDep({
    ext: "post-grid-category-filter",
    deps: [activeCategoryId],
  });

  return (
    <Grid
      postsPerPage={posts_per_page}
      initialItems={initialPosts}
      initialTotalPosts={initialTotalPosts}
      refreshKey={refreshKey}
      fetchItems={({ page, postsPerPage, offset, abortController }) => {
        return fetchPosts({
          page,
          postsPerPage,
          offset,
          module: data,
          locale,
          fallback_options,
          fallback_image,
          abortController,
          categoryOverride: activeCategoryId ? [activeCategoryId] : undefined,
          excludeIds: excludePostIds,
        });
      }}
      renderLoading={{ numberOfItemsWhileLoading: posts_per_page }}
    >
      {(gridPosts, loading) => {
        return (
          <>
            {showCategoryFilter && (
              <GridCategoryFilter
                categories={categories}
                activeCategoryId={activeCategoryId}
                onCategoryChange={setActiveCategoryId}
              />
            )}
            <div className="py-4">
              <GridContent justifyContent="center" gap="2rem">
                {gridPosts.map((element, key) => {
                  return (
                    <GridItem itemWidth={itemWidth} key={key}>
                      <PostCard
                        skeleton={loading}
                        element={
                          loading ? mockPost : (element as PostsGridElementType)
                        }
                        border_radius={show_border_radius}
                        box_shadow={show_box_shadow}
                        showBox={show_card_box}
                        media_format={media_format}
                        allCategories={categories}
                        getLabel={getLabel}
                        columnNumbers={columnNumbers}
                      />
                    </GridItem>
                  );
                })}
              </GridContent>
            </div>

            {!hidePagination && (
              <GridLoadMore
                grid_navigation={grid_navigation}
                buttonStyles={buttonStyles}
                iconPosition="left"
                scrollTargetSelector={scrollTargetSelector}
              />
            )}
          </>
        );
      }}
    </Grid>
  );
};

{
  /* jscpd:ignore-end */
}

export default PostsGridModuleClient;
